/**
 * Pure list builders behind the brand/model type-ahead (ENG-4831).
 *
 * Kept out of the components so the "which rows does the user see" rules —
 * the generic "Other" brand is hidden, combined "Brand · Model" rows appear
 * only while searching — are unit-testable without rendering a popover.
 */
import { MatchScore, normalize, rankMatches, scoreMatch } from '@studio/design-system/combobox';
import type { Brand as M_Brand, Model as M_Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Collection } from '#~/api/device/registry/index.js';

/** Row in the brand field: a brand, or a combined "Brand · Model" match (B3). */
export type BrandRow =
  | { brand: M_Brand; kind: 'brand' }
  | { brand: M_Brand; kind: 'model'; model: M_Model };

/** Models per brand, as returned by `useModelsByBrands`. */
export type ModelsByBrand = Record<M_Brand['id'], T_Maybe<Collection<M_Model>>>;

/** Shortest (normalised) query that may surface combined rows; 1 char matches everything. */
export const MinCombinedQueryLength = 2;

/** Cap on combined rows so a short query cannot bury the brand list. */
export const MaxCombinedRows = 8;

/** Trailing-action copy shared by the brand and model fields (Board B5). */
export const NotListedActionLabel = "My camera isn't listed";

/** Copy for the generic brand/model when it is the current value (C3 "Not listed"). */
export const NotListedLabel = 'Not listed';

/**
 * The registry's generic brand/model is named "Other" (registry `generic.go`).
 * Same `includes('other')` test the Select-based pickers used, so nothing that
 * was treated as "Other" before changes meaning now.
 */
export const isOtherName = (name: string): boolean => name.toLowerCase().includes('other');

/** Case-insensitive alphabetical order by `name` (B1: brands listed A→Z). */
const byName = (a: { name: string }, b: { name: string }): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Brand rows for the brand field: every brand A→Z without the generic "Other"
 * brand, whose job moved to the "My camera isn't listed" trailing action.
 * The generic brand stays (last) only while it is the committed value, so a
 * saved "Other" device still shows what it is instead of an empty field.
 */
export const getBrandRows = (
  brands: T_Maybe<Collection<M_Brand>>,
  selectedId: T_Maybe<M_Brand['id']>
): BrandRow[] => {
  const all = Object.values(brands?.items ?? {});
  const listed = all.filter((µ) => !isOtherName(µ.name)).sort(byName);
  const other = all.find((µ) => isOtherName(µ.name) && µ.id === selectedId);

  return [...listed, ...(other ? [other] : [])].map((brand) => ({ brand, kind: 'brand' }));
};

/**
 * Combined "Brand · Model" rows for a brand-field query (B3: "srg-a40" offers
 * "Sony · SRG-A40"). Only brands whose own name does NOT match the query
 * contribute, so typing "sony" lists the brand rather than every Sony model;
 * typing "sony fx6" still works because no brand is named that.
 *
 * A combined row needs at least a one-letter-slip (`typo`) match. Scattered
 * letters are too loose across a long "Brand Model" key: "sony" is a
 * subsequence of "Cre(s)tr(o)n 1 Be(y)ond…", which listed every Crestron
 * 1 Beyond camera under a "sony" search (ENG-4831 ITB feedback). Brand rows
 * and the model field still match subsequences ("sny" → Sony).
 */
export const getCombinedRows = (
  query: string,
  brands: T_Maybe<Collection<M_Brand>>,
  modelsByBrand: T_Maybe<ModelsByBrand>
): BrandRow[] => {
  if (normalize(query).length < MinCombinedQueryLength || !modelsByBrand) {
    return [];
  }

  const candidates = Object.values(brands?.items ?? {})
    .filter((brand) => !isOtherName(brand.name))
    .filter((brand) => rankMatches(query, [brand], (µ) => [µ.name]).length === 0)
    .flatMap((brand) =>
      Object.values(modelsByBrand[brand.id]?.items ?? {})
        .filter((model) => !isOtherName(model.name))
        .map((model): BrandRow => ({ brand, kind: 'model', model }))
    );

  return rankMatches(
    query,
    candidates.filter((row) =>
      getBrandRowSearchKeys(row).some((key) => scoreMatch(query, key) >= MatchScore.typo)
    ),
    getBrandRowSearchKeys
  ).slice(0, MaxCombinedRows);
};

/** Searchable strings for a brand-field row. */
export const getBrandRowSearchKeys = (row: BrandRow): string[] =>
  row.kind === 'brand' ? [row.brand.name] : [row.model.name, `${row.brand.name} ${row.model.name}`];

/**
 * Model rows in registry order without the brand's generic "Other" model
 * (ENG-4831 / ENG-4832): the only way to say "not listed" is the trailing
 * "My camera isn't listed" action, so the generic model is never a visible
 * choice. It stays selectable programmatically — `findOther` still finds it
 * for the flow's Continue, and the settings panel commits it for the Other
 * brand from the raw collection.
 */
export const getModelRows = (models: T_Maybe<Collection<M_Model>>): M_Model[] =>
  Object.values(models?.items ?? {}).filter((µ) => !isOtherName(µ.name));

/**
 * The generic "Other" entry of a collection: by name first (the rule the list
 * filters use, so the two always agree), else the registry's `generic` field.
 */
export const findOther = <T extends { name: string }>(
  collection: T_Maybe<{ generic?: T; items: Record<string, T> }>
): T_Maybe<T> =>
  Object.values(collection?.items ?? {}).find((µ) => isOtherName(µ.name)) ?? collection?.generic;
